// The iframe renders the existing Lux workspace; this host only manages its bounds.
function initializeAiChatWorkspace() {
  if (window.top !== window || !validHostPattern.test(window.location.host)) {
    return
  }
  if (/^\/(?:injects|login)(?:\/|$)/.test(window.location.pathname)) return
  if (
    window.location.pathname === '/dashboard' ||
    /^\/profit-and-loss(?:\/|$)/.test(window.location.pathname)
  ) {
    return
  }
  if (typeof window.__luxAiChatCleanup === 'function') {
    window.__luxAiChatCleanup()
  }

  const origin = new URL(baseApiUrl).origin
  let frame = null
  let email = ''
  let previousAuth
  let open = false
  let position = { right: 0, bottom: 0 }
  let drag = null
  let size = null
  const placeFrame = (right, bottom, persist = false) => {
    if (!frame || !open) return
    const bounds = frame.getBoundingClientRect()
    if (size) {
      size = {
        width: Math.max(
          Math.min(320, Math.max(0, window.innerWidth - 32)),
          Math.min(size.width, window.innerWidth - 32)
        ),
        height: Math.max(
          Math.min(320, Math.max(0, window.innerHeight - 32)),
          Math.min(size.height, window.innerHeight - 32)
        ),
      }
      frame.style.width = `${size.width + 32}px`
      frame.style.height = `${size.height + 32}px`
    }
    position = {
      right: Math.max(
        0,
        Math.min(
          right,
          window.innerWidth - (size ? size.width + 32 : bounds.width)
        )
      ),
      bottom: Math.max(
        0,
        Math.min(
          bottom,
          window.innerHeight - (size ? size.height + 32 : bounds.height)
        )
      ),
    }
    frame.style.right = `${position.right}px`
    frame.style.bottom = `${position.bottom}px`
    if (persist) {
      frame.contentWindow?.postMessage(
        {
          type: 'lux-ai-chat-position',
          right: position.right + 16,
          bottom: position.bottom + 16,
          width: size ? size.width : bounds.width - 32,
          height: size ? size.height : bounds.height - 32,
        },
        origin
      )
    }
  }

  const placeLauncher = () => {
    if (!frame || open) return
    frame.style.left = '0'
    frame.style.right = 'auto'
    frame.style.bottom = '0'
  }

  const onResize = () => {
    if (!open) {
      placeLauncher()
      return
    }
    const previous = position
    const previousSize = size
    placeFrame(position.right, position.bottom)
    if (
      previous.right !== position.right ||
      previous.bottom !== position.bottom ||
      previousSize?.width !== size?.width ||
      previousSize?.height !== size?.height
    ) {
      placeFrame(position.right, position.bottom, true)
    }
    drag = null
  }

  const sendHost = () => {
    frame?.contentWindow?.postMessage(
      { type: 'lux-ai-chat-host', email },
      origin
    )
  }

  const syncAuth = () => {
    let raw = null
    let nextEmail = ''
    try {
      raw = localStorage.getItem('auth')
      const auth = JSON.parse(raw)
      if (typeof auth?.email === 'string') {
        nextEmail = auth.email.trim().toLowerCase()
      }
    } catch (_) {
      // Malformed or unavailable auth must never expose another user's workspace.
    }
    if (raw === previousAuth) return
    previousAuth = raw
    if (email !== nextEmail) {
      frame?.remove()
      frame = null
      open = false
      size = null
      drag = null
      position = { right: 0, bottom: 0 }
    }
    email = nextEmail
    if (!email || frame || !document.body) return

    frame = document.createElement('iframe')
    frame.id = 'lux-ai-chat-workspace'
    frame.title = 'AI Chat'
    frame.src = `${baseApiUrl}/injects/ai-chat`
    frame.referrerPolicy = 'strict-origin-when-cross-origin'
    Object.assign(frame.style, {
      position: 'fixed',
      left: '0',
      bottom: '0',
      border: '0',
      width: '115px',
      height: '115px',
      zIndex: '999999',
      display: 'none',
      background: 'transparent',
    })
    frame.addEventListener('load', sendHost)
    document.body.appendChild(frame)
  }

  const onMessage = (event) => {
    if (
      !frame ||
      event.source !== frame.contentWindow ||
      event.origin !== origin
    ) {
      return
    }
    const state = event.data
    if (state?.type === 'lux-ai-chat-ready') {
      sendHost()
      return
    }
    if (state?.type === 'lux-ai-chat-position') {
      const requested = state.position
      if (
        drag ||
        !requested ||
        !Number.isFinite(requested.right) ||
        !Number.isFinite(requested.bottom)
      ) {
        return
      }
      if (requested.width !== undefined || requested.height !== undefined) {
        if (
          !Number.isFinite(requested.width) ||
          !Number.isFinite(requested.height) ||
          requested.width <= 0 ||
          requested.height <= 0
        ) {
          return
        }
        size = { width: requested.width, height: requested.height }
      }
      position = { right: requested.right - 16, bottom: requested.bottom - 16 }
      if (open) onResize()
      return
    }
    if (state?.type === 'lux-ai-chat-drag') {
      if (
        !open ||
        frame.style.display === 'none' ||
        !Number.isFinite(state.x) ||
        !Number.isFinite(state.y)
      ) {
        return
      }
      if (state.phase === 'start') {
        if (
          state.mode !== undefined &&
          state.mode !== 'move' &&
          state.mode !== 'resize'
        ) {
          return
        }
        if (
          state.mode === 'resize' &&
          !['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'].includes(state.edge)
        ) {
          return
        }
        const bounds = frame.getBoundingClientRect()
        const left = Number.isFinite(bounds.left)
          ? bounds.left
          : window.innerWidth - bounds.width - position.right
        const top = Number.isFinite(bounds.top)
          ? bounds.top
          : window.innerHeight - bounds.height - position.bottom
        drag = {
          x: state.x,
          y: state.y,
          lastX: state.x,
          lastY: state.y,
          left,
          top,
          width: bounds.width,
          height: bounds.height,
          right: window.innerWidth - left - bounds.width,
          bottom: window.innerHeight - top - bounds.height,
          mode: state.mode || 'move',
          edge: state.edge,
        }
      } else if (drag && (state.phase === 'move' || state.phase === 'end')) {
        if (state.phase === 'move') {
          drag.lastX = state.x
          drag.lastY = state.y
        }
        const dx = drag.lastX - drag.x
        const dy = drag.lastY - drag.y
        if (drag.mode === 'resize') {
          let left = drag.left
          let top = drag.top
          let right = left + drag.width
          let bottom = top + drag.height
          const minWidth = Math.min(352, window.innerWidth)
          const minHeight = Math.min(352, window.innerHeight)
          if (drag.edge.includes('w')) {
            left = Math.max(0, Math.min(left + dx, right - minWidth))
          }
          if (drag.edge.includes('e')) {
            right = Math.min(
              window.innerWidth,
              Math.max(right + dx, left + minWidth)
            )
          }
          if (drag.edge.includes('n')) {
            top = Math.max(0, Math.min(top + dy, bottom - minHeight))
          }
          if (drag.edge.includes('s')) {
            bottom = Math.min(
              window.innerHeight,
              Math.max(bottom + dy, top + minHeight)
            )
          }
          size = { width: right - left - 32, height: bottom - top - 32 }
          placeFrame(
            window.innerWidth - right,
            window.innerHeight - bottom,
            state.phase === 'end'
          )
        } else {
          placeFrame(drag.right - dx, drag.bottom - dy, state.phase === 'end')
        }
        if (state.phase === 'end') drag = null
      }
      return
    }
    if (
      state?.type !== 'lux-ai-chat-state' ||
      typeof state.visible !== 'boolean' ||
      typeof state.open !== 'boolean'
    ) {
      return
    }
    open = state.open
    if (!open || !state.visible) drag = null
    frame.style.display = state.visible ? 'block' : 'none'
    frame.style.width = state.open ? 'min(1056px, 100vw)' : '115px'
    frame.style.left = state.open ? 'auto' : '0'
    frame.style.right = state.open ? '0' : 'auto'
    frame.style.top = 'auto'
    frame.style.bottom = '0'
    frame.style.transform = 'none'
    frame.style.height = state.open
      ? 'min(calc(80vh + 2rem), 872px, 100dvh)'
      : '115px'
    if (open) {
      if (drag) placeFrame(position.right, position.bottom)
      else onResize()
    } else placeLauncher()
  }

  const onOutsidePointerDown = (event) => {
    if (
      frame &&
      open &&
      frame.style.display !== 'none' &&
      event.target !== frame
    ) {
      const control = event.target?.closest?.(
        'a[href],button,input,textarea,select,label,summary,[contenteditable]:not([contenteditable="false"]),[role="button"],[role="link"],[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"],[role="tab"],[role="checkbox"],[role="radio"],[role="switch"],[role="combobox"],[role="option"],[role="slider"],[role="treeitem"],[data-chat-keep-open]'
      )
      if (control && !control.matches(':disabled,[aria-disabled="true"]')) {
        return
      }
      frame.contentWindow?.postMessage({ type: 'lux-ai-chat-minimize' }, origin)
    }
  }

  window.addEventListener('pointerdown', onOutsidePointerDown, true)
  window.addEventListener('message', onMessage)
  window.addEventListener('storage', syncAuth)
  window.addEventListener('focus', syncAuth)
  window.addEventListener('resize', onResize)
  window.__luxAiChatCleanup = () => {
    window.removeEventListener('pointerdown', onOutsidePointerDown, true)
    window.removeEventListener('message', onMessage)
    window.removeEventListener('storage', syncAuth)
    window.removeEventListener('focus', syncAuth)
    window.removeEventListener('resize', onResize)
    frame?.remove()
    frame = null
    delete window.__luxAiChatCleanup
  }
  syncAuth()
}
